import React, { useState, useRef, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Toolbar, Tooltip } from '@mui/material';
import Draggable from 'react-draggable';
import Paper from '@mui/material/Paper';
import Minimize from '@mui/icons-material/Minimize';
import OpenInFull from '@mui/icons-material/OpenInFull';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const IframeComponent = React.memo(React.forwardRef(({ title, url }, ref) => (
    <iframe
        ref={ref}
        src={url}
        style={{
            width: '100%',
            height: '100%',
            border: 'none',
            overflow: 'hidden',
        }}
        title={title}
        loading="lazy"
    />
)));

const PaperComponent = ({ defaultPosition, ...props }) => {
    return (
        <Draggable
            handle="#webpage-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
            defaultPosition={defaultPosition}
        >
            <Paper {...props} />
        </Draggable>
    );
};

const WebpageDialog = ({ title, url, onClose }) => {

    const [isFullScreen, setIsFullScreen] = useState(false);
    const [defaultPosition, setDefaultPosition] = useState({ x: 0, y: 0 });
    const iframeRef = useRef(null);

    const handleToggleFullScreen = () => {
        setIsFullScreen(prev => !prev);
    };

    const openInNewTab = () => {
        onClose();
        window.open(url, '_blank');
    };

    useEffect(() => {
        if (!isFullScreen) {
            setDefaultPosition({ x: 0, y: 0 });
        }
    }, [isFullScreen]);

    return (
        <Dialog
            disablePortal
            open={true}
            fullScreen={isFullScreen}
            fullWidth
            scroll="paper"
            PaperComponent={(props) => (
                <PaperComponent {...props} defaultPosition={defaultPosition} />
            )}
            PaperProps={{
                style: {
                    overflow: 'hidden',
                }
            }}
            sx={{
                '& .MuiDialog-paper': {
                    width: isFullScreen ? '100%' : '80%',
                    height: isFullScreen ? '100%' : '80%',
                    maxWidth: isFullScreen ? '100%' : '1200px',
                    maxHeight: isFullScreen ? '100%' : '800px',
                    overflow: 'hidden',
                },
                overflow: 'hidden',
            }}
            BackdropProps={{
                sx: {
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                },
            }}
        >
            <Toolbar
                id="webpage-dialog-title"
                sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    cursor: isFullScreen ? 'default' : 'move',
                    userSelect: 'none',
                    m: 0,
                    p: 0,
                    display: 'flex'
                }}
            >
                <DialogTitle sx={{ flexGrow: 1 }}>
                    {title || "Webpage"}
                </DialogTitle>

                <Tooltip title="Open in new tab">
                    <IconButton onClick={openInNewTab} sx={{ color: (theme) => theme.palette.grey[500] }}>
                        <OpenInNewIcon />
                    </IconButton>
                </Tooltip>
                <IconButton onClick={handleToggleFullScreen} sx={{ color: (theme) => theme.palette.grey[500] }}>
                    {isFullScreen ? <Minimize /> : <OpenInFull />}
                </IconButton>
                <IconButton onClick={onClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
                    <CloseIcon />
                </IconButton>
            </Toolbar>

            <DialogContent dividers sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
                <IframeComponent ref={iframeRef} title={title} url={url} />
            </DialogContent>
        </Dialog>
    );
};

export default WebpageDialog;
